/**
 * SeedContext implementation.
 * Provides typed helpers for seeding data in the onSeed hook.
 *
 * IMPORTANT: This file lives in db/ and MUST NOT import from modules/.
 * All module-specific deps are injected via SeedContextDeps.
 */
import type { Knex } from 'knex'
import type { SeedContext } from '@gzl10/nexus-sdk'

/** Minimal interface for master registry (avoids importing from modules/) */
interface MasterRegistryLike {
  register(type: string, entries: Array<{
    code: string
    label: string | { [key: string]: string | undefined }
    order?: number
    is_active?: boolean
    metadata?: Record<string, unknown>
  }>, options?: { seed?: 'if-empty' | 'always' }): void
}

export interface SeedContextDeps {
  knex: Knex
  generateId: () => string
  hashPassword: (password: string) => Promise<string>
  masterRegistry: MasterRegistryLike
  /** Map of plugin code/name → table prefix (e.g. 'tag' → 'tag_', 'tags' → 'tag_') */
  pluginPrefixes: Map<string, string>
  logger: { info: (...args: any[]) => void; warn: (...args: any[]) => void }
  /** Callback to collect accumulated CASL permissions after onSeed completes */
  onPermissionsCollected?: (perms: Map<string, Map<string, string[]>>) => void
}

export function createSeedContext(deps: SeedContextDeps): { ctx: SeedContext; flushPermissions: () => void } {
  const { knex: db, generateId, hashPassword, pluginPrefixes, logger } = deps

  /** Accumulated permissions: Map<roleName, Map<subject, actions[]>> */
  const permissionsMap = new Map<string, Map<string, string[]>>()

  const seedContext: SeedContext = {
    masters: {
      register(type, entries, options) {
        deps.masterRegistry.register(type, entries, options)
      }
    },

    roles: {
      async add(role) {
        const hasTable = await db.schema.hasTable('roles')
        if (!hasTable) return

        // Insert role if not exists (idempotent by name)
        const existing = await db('roles').where({ name: role.name }).first()
        if (!existing) {
          const description = role.description
            ? JSON.stringify(typeof role.description === 'string' ? { en: role.description } : role.description)
            : null

          await db('roles').insert({
            id: generateId(),
            name: role.name,
            description
          })
          logger.info({ role: role.name }, 'Seeded role')
        }

        // Accumulate CASL permissions (always, even if role already existed)
        if (role.permissions) {
          let rolePerms = permissionsMap.get(role.name)
          if (!rolePerms) {
            rolePerms = new Map()
            permissionsMap.set(role.name, rolePerms)
          }
          for (const [subject, actions] of Object.entries(role.permissions)) {
            const normalized = subject.charAt(0).toUpperCase() + subject.slice(1)
            rolePerms.set(normalized, actions)
          }
        }
      }
    },

    users: {
      async add(user) {
        const hasTable = await db.schema.hasTable('users')
        if (!hasTable) return

        const existing = await db('users').where({ email: user.email }).first()
        if (existing) return

        const userId = generateId()
        await db('users').insert({
          id: userId,
          type: user.type ?? 'human',
          email: user.email,
          password: await hashPassword(user.password),
          name: user.name ?? user.email
        })

        if (user.roles?.length) {
          for (const roleName of user.roles) {
            const role = await db('roles').where({ name: roleName }).first<{ id: string }>()
            if (role) {
              await db('user_roles').insert({
                id: generateId(),
                user_id: userId,
                role_id: role.id
              })
            } else {
              logger.warn({ role: roleName, user: user.email }, 'Role not found, skipping assignment')
            }
          }
        }

        logger.info({ email: user.email, roles: user.roles }, 'Seeded user')
      }
    },

    plugin(pluginName) {
      return {
        entity(entityName) {
          return {
            async upsert(data, options) {
              // Resolve prefix from pluginPrefixes map
              let prefix = pluginPrefixes.get(pluginName) ?? ''
              if (!prefix) {
                for (const [key, pfx] of pluginPrefixes) {
                  if (pluginName === key || pluginName.endsWith(`-${key}`)) {
                    prefix = pfx
                    break
                  }
                }
              }

              const fullTable = `${prefix}${entityName}`

              const hasTable = await db.schema.hasTable(fullTable)
              if (!hasTable) {
                logger.warn({ table: fullTable, plugin: pluginName }, 'Table not found for plugin entity seed')
                return 0
              }

              const key = options?.key ?? 'id'
              let seeded = 0

              for (const row of data) {
                const keyValue = row[key]
                if (keyValue != null) {
                  const existing = await db(fullTable).where({ [key]: keyValue }).first()
                  if (existing) continue
                }

                const insertData = { ...row }
                if (key === 'id' && !insertData['id']) {
                  insertData['id'] = generateId()
                }

                await db(fullTable).insert(insertData)
                seeded++
              }

              if (seeded > 0) {
                logger.info({ table: fullTable, seeded }, 'Seeded plugin entity data')
              }
              return seeded
            }
          }
        }
      }
    },

    async raw(table, data) {
      logger.warn({ table }, 'Using seed.raw() — prefer typed helpers when available')

      const hasTable = await db.schema.hasTable(table)
      if (!hasTable) {
        logger.warn({ table }, 'Table not found for raw seed')
        return 0
      }

      const rows = Array.isArray(data) ? data : [data]
      await db(table).insert(rows)
      return rows.length
    }
  }

  return {
    ctx: seedContext,
    flushPermissions: () => {
      if (permissionsMap.size > 0 && deps.onPermissionsCollected) {
        deps.onPermissionsCollected(permissionsMap)
      }
    }
  }
}
